/**
 * BomAggregatePage.tsx
 *
 * Read-only Aggregate BOM module (Tổng hợp Định mức) in "Danh mục" menu.
 * Displays all product BOM configs with status 'OK', flattened via Cartesian product into:
 * 1. BOM Gỗ
 * 2. BOM Ngũ Kim
 *
 * Features:
 * - 2 tabs with row count badges.
 * - Excel-style per-column substring filtering with quick reset.
 * - Excel (.xlsx) export with 2 sheets containing currently filtered rows.
 * - STT column on the left (1-indexed based on display index).
 * - Full Inter font compliance and system design tokens.
 */
import { useEffect, useMemo, useState } from 'react'
import { Download, RotateCcw, Search, X } from 'lucide-react'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { PageError, PageLoading } from '@/components/ui/PageAsyncState'
import { TopScrollSync } from '@/components/table/TopScrollSync'
import { useDragScroll } from '@/hooks/useDragScroll'
import {
  getBomAggregateApi,
  type BomAggregateConfigItem,
} from '@/services/boms/productBomSpreadsheetApiClient'
import { flattenBomConfigs } from './flattenBom'
import { filterFlatBomRows } from './filterBom'
import { exportBomAggregateToExcel } from './bomAggregateExport'
import type { FlatBomWoodRow, FlatBomHardwareRow } from './types'

type ActiveTab = 'wood' | 'hardware'

interface ColumnDef<T> {
  id: keyof T | 'stt'
  header: string
  width?: number
  align?: 'left' | 'right' | 'center'
  render?: (row: T, index: number) => React.ReactNode
}

const WOOD_COLUMNS: ColumnDef<FlatBomWoodRow>[] = [
  {
    id: 'stt',
    header: 'STT',
    width: 55,
    align: 'center',
    render: (_, index) => <span className="font-semibold text-slate-500">{index + 1}</span>,
  },
  {
    id: 'code',
    header: 'Mã',
    width: 120,
    render: (r) => <span className="font-medium text-slate-900">{r.code}</span>,
  },
  {
    id: 'color',
    header: 'Màu',
    width: 110,
    render: (r) => <span className="text-slate-700">{r.color}</span>,
  },
  {
    id: 'wood',
    header: 'Gỗ',
    width: 110,
    render: (r) => <span className="text-slate-700">{r.wood}</span>,
  },
  {
    id: 'assembly',
    header: 'Kết cấu',
    width: 100,
    render: (r) => <span className="text-slate-700">{r.assembly}</span>,
  },
  {
    id: 'part_name',
    header: 'Tên Chi Tiết',
    width: 180,
    render: (r) => <span className="font-medium text-slate-900">{r.part_name}</span>,
  },
  {
    id: 'wood_type_raw',
    header: 'Loại Gỗ',
    width: 130,
    render: (r) => <span className="text-slate-700">{r.wood_type_raw}</span>,
  },
  {
    id: 'dims_finished_t',
    header: 'DÀY',
    width: 75,
    align: 'right',
    render: (r) => (
      <span className="font-mono text-xs text-slate-700">{r.dims_finished_t}</span>
    ),
  },
  {
    id: 'dims_finished_w',
    header: 'RỘNG',
    width: 75,
    align: 'right',
    render: (r) => (
      <span className="font-mono text-xs text-slate-700">{r.dims_finished_w}</span>
    ),
  },
  {
    id: 'dims_finished_l',
    header: 'DÀI',
    width: 75,
    align: 'right',
    render: (r) => (
      <span className="font-mono text-xs text-slate-700">{r.dims_finished_l}</span>
    ),
  },
  {
    id: 'tenon',
    header: 'Mộng',
    width: 110,
    render: (r) => <span className="text-slate-700">{r.tenon}</span>,
  },
  {
    id: 'qty_per',
    header: 'SL',
    width: 70,
    align: 'right',
    render: (r) => (
      <span className="font-mono font-medium text-slate-900">{r.qty_per}</span>
    ),
  },
  {
    id: 'veneer_spec',
    header: 'Veneer',
    width: 140,
    render: (r) => <span className="text-slate-700">{r.veneer_spec}</span>,
  },
  {
    id: 'notes',
    header: 'Ghi chú',
    width: 160,
    render: (r) => <span className="text-slate-500">{r.notes}</span>,
  },
]

const HARDWARE_COLUMNS: ColumnDef<FlatBomHardwareRow>[] = [
  {
    id: 'stt',
    header: 'STT',
    width: 55,
    align: 'center',
    render: (_, index) => <span className="font-semibold text-slate-500">{index + 1}</span>,
  },
  {
    id: 'code',
    header: 'Mã',
    width: 120,
    render: (r) => <span className="font-medium text-slate-900">{r.code}</span>,
  },
  {
    id: 'color',
    header: 'Màu',
    width: 110,
    render: (r) => <span className="text-slate-700">{r.color}</span>,
  },
  {
    id: 'wood',
    header: 'Gỗ',
    width: 110,
    render: (r) => <span className="text-slate-700">{r.wood}</span>,
  },
  {
    id: 'assembly',
    header: 'Kết cấu',
    width: 100,
    render: (r) => <span className="text-slate-700">{r.assembly}</span>,
  },
  {
    id: 'name_raw',
    header: 'Tên Ngũ Kim',
    width: 180,
    render: (r) => <span className="font-medium text-slate-900">{r.name_raw}</span>,
  },
  {
    id: 'spec_raw',
    header: 'Quy cách',
    width: 140,
    render: (r) => <span className="text-slate-700">{r.spec_raw}</span>,
  },
  {
    id: 'qty_per',
    header: 'SL',
    width: 70,
    align: 'right',
    render: (r) => (
      <span className="font-mono font-medium text-slate-900">{r.qty_per}</span>
    ),
  },
  {
    id: 'uom_hint',
    header: 'ĐVT',
    width: 75,
    align: 'center',
    render: (r) => <span className="text-slate-700">{r.uom_hint}</span>,
  },
  {
    id: 'color_hint',
    header: 'Màu NK',
    width: 95,
    render: (r) => <span className="text-slate-700">{r.color_hint}</span>,
  },
  {
    id: 'item_type',
    header: 'Loại',
    width: 110,
    render: (r) => <span className="text-slate-700">{r.item_type}</span>,
  },
  {
    id: 'notes',
    header: 'Ghi chú',
    width: 160,
    render: (r) => <span className="text-slate-500">{r.notes}</span>,
  },
]

export function BomAggregatePage({
  initialConfigs,
}: {
  initialConfigs?: BomAggregateConfigItem[]
} = {}) {
  const [configs, setConfigs] = useState<BomAggregateConfigItem[]>(initialConfigs ?? [])
  const [loading, setLoading] = useState(!initialConfigs)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<ActiveTab>('wood')

  // Per-column filters
  const [woodFilters, setWoodFilters] = useState<Record<string, string>>({})
  const [hwFilters, setHwFilters] = useState<Record<string, string>>({})

  const tableScrollRef = useDragScroll<HTMLDivElement>()

  async function loadData() {
    setLoading(true)
    setError(null)
    try {
      const data = await getBomAggregateApi()
      setConfigs(data.configs)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Không thể tải dữ liệu tổng hợp định mức.',
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (initialConfigs) return
    void loadData()
  }, [initialConfigs])

  // Flattened data
  const { allWoodRows, allHardwareRows } = useMemo(() => {
    const { woodRows, hardwareRows } = flattenBomConfigs(configs)
    return { allWoodRows: woodRows, allHardwareRows: hardwareRows }
  }, [configs])

  // Filtered rows for BOM Gỗ
  const filteredWoodRows = useMemo(() => {
    return filterFlatBomRows(allWoodRows, woodFilters)
  }, [allWoodRows, woodFilters])

  // Filtered rows for BOM Ngũ Kim
  const filteredHardwareRows = useMemo(() => {
    return filterFlatBomRows(allHardwareRows, hwFilters)
  }, [allHardwareRows, hwFilters])

  // Check if current tab has any active filter
  const hasActiveWoodFilter = useMemo(() => {
    return Object.values(woodFilters).some((v) => Boolean(v && v.trim()))
  }, [woodFilters])

  const hasActiveHwFilter = useMemo(() => {
    return Object.values(hwFilters).some((v) => Boolean(v && v.trim()))
  }, [hwFilters])

  const hasActiveFilter = activeTab === 'wood' ? hasActiveWoodFilter : hasActiveHwFilter

  const resetCurrentTabFilters = () => {
    if (activeTab === 'wood') {
      setWoodFilters({})
    } else {
      setHwFilters({})
    }
  }

  const resetAllFilters = () => {
    setWoodFilters({})
    setHwFilters({})
  }

  // Handle Excel Export
  const handleExportExcel = () => {
    exportBomAggregateToExcel(filteredWoodRows, filteredHardwareRows)
  }

  const currentColumns = activeTab === 'wood' ? WOOD_COLUMNS : HARDWARE_COLUMNS
  const currentFilteredRows = activeTab === 'wood' ? filteredWoodRows : filteredHardwareRows
  const currentTotalRows = activeTab === 'wood' ? allWoodRows : allHardwareRows
  const currentFilters = activeTab === 'wood' ? woodFilters : hwFilters
  const setCurrentFilter = (colId: string, value: string) => {
    if (activeTab === 'wood') {
      setWoodFilters((prev) => ({ ...prev, [colId]: value }))
    } else {
      setHwFilters((prev) => ({ ...prev, [colId]: value }))
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <Breadcrumb
        items={[
          { label: 'Danh mục', to: '/catalog' },
          { label: 'Tổng hợp Định mức' },
        ]}
      />

      <PageHeader
        title="Tổng hợp Định mức (BOM)"
        subtitle="Bảng phẳng tổng hợp tất cả cấu hình định mức có trạng thái OK từ mọi sản phẩm. Dữ liệu READ-ONLY phục vụ tra cứu và kiểm toán."
        actions={
          <Button
            variant="primary"
            size="sm"
            onClick={handleExportExcel}
            disabled={loading || (allWoodRows.length === 0 && allHardwareRows.length === 0)}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Xuất Excel (.xlsx)
          </Button>
        }
      />

      {loading ? (
        <PageLoading label="Đang tải dữ liệu tổng hợp định mức..." />
      ) : error ? (
        <PageError message={error} onRetry={loadData} />
      ) : configs.length === 0 ? (
        <EmptyState
          title="Chưa có định mức nào có trạng thái OK"
          description="Hệ thống chỉ tổng hợp các định mức có trạng thái OK từ các cấu hình BOM của sản phẩm."
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          {/* Tabs and Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setActiveTab('wood')}
                className={`flex items-center gap-2 rounded-[var(--radius-sm)] px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  activeTab === 'wood'
                    ? 'bg-[var(--color-accent)] text-white shadow-sm'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <span>BOM Gỗ</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    activeTab === 'wood'
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-200 text-slate-700'
                  }`}
                >
                  {allWoodRows.length}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('hardware')}
                className={`flex items-center gap-2 rounded-[var(--radius-sm)] px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  activeTab === 'hardware'
                    ? 'bg-[var(--color-accent)] text-white shadow-sm'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <span>BOM Ngũ Kim</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    activeTab === 'hardware'
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-200 text-slate-700'
                  }`}
                >
                  {allHardwareRows.length}
                </span>
              </button>
            </div>

            {/* Filter stats & Reset */}
            <div className="flex items-center gap-3 text-xs">
              <span className="text-slate-600">
                Hiển thị{' '}
                <strong className="font-semibold text-slate-900">
                  {currentFilteredRows.length}
                </strong>{' '}
                /{' '}
                <span className="text-slate-500">{currentTotalRows.length}</span> dòng
              </span>

              {hasActiveFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetCurrentTabFilters}
                  className="h-7 gap-1 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  <RotateCcw className="h-3 w-3" />
                  Xóa bộ lọc tab này
                </Button>
              )}

              {(hasActiveWoodFilter || hasActiveHwFilter) && (
                <button
                  type="button"
                  onClick={resetAllFilters}
                  className="text-xs text-slate-500 underline hover:text-slate-800"
                >
                  Xóa tất cả bộ lọc
                </button>
              )}
            </div>
          </div>

          {/* Table Container */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)]">
            {/* Top scrollbar synchronization for comfortable horizontal navigation */}
            <TopScrollSync targetRef={tableScrollRef} />

            <div
              ref={tableScrollRef}
              className="min-h-0 flex-1 overflow-auto"
            >
              <table className="w-full min-w-[1100px] border-collapse text-left text-sm">
                <thead className="sticky top-0 z-10 bg-slate-100 shadow-sm">
                  {/* Row 1: Headers */}
                  <tr className="border-b border-slate-300 text-xs font-semibold tracking-wide text-slate-700 uppercase">
                    {currentColumns.map((col) => {
                      const alignClass =
                        col.align === 'right'
                          ? 'text-right'
                          : col.align === 'center'
                            ? 'text-center'
                            : 'text-left'
                      return (
                        <th
                          key={String(col.id)}
                          style={col.width ? { width: col.width, minWidth: col.width } : undefined}
                          className={`px-2.5 py-2 whitespace-nowrap ${alignClass}`}
                        >
                          {col.header}
                        </th>
                      )
                    })}
                  </tr>

                  {/* Row 2: Per-column Excel filter row */}
                  <tr className="border-b border-slate-200 bg-slate-50">
                    {currentColumns.map((col) => {
                      const colKey = String(col.id)
                      if (colKey === 'stt') {
                        return (
                          <th key="filter-stt" className="px-2 py-1 text-center">
                            <span className="text-[10px] text-slate-400 font-normal">Lọc</span>
                          </th>
                        )
                      }
                      const filterVal = currentFilters[colKey] || ''
                      return (
                        <th
                          key={`filter-${colKey}`}
                          style={col.width ? { width: col.width, minWidth: col.width } : undefined}
                          className="px-1.5 py-1 font-normal"
                        >
                          <div className="relative flex items-center">
                            <input
                              type="text"
                              value={filterVal}
                              onChange={(e) => setCurrentFilter(colKey, e.target.value)}
                              placeholder={`Lọc ${col.header.toLowerCase()}...`}
                              className={`h-7 w-full rounded border px-2 pr-6 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 ${
                                filterVal
                                  ? 'border-[var(--color-accent)] bg-blue-50/40 focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)]'
                                  : 'border-slate-300 bg-white focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)]'
                              }`}
                            />
                            {filterVal ? (
                              <button
                                type="button"
                                onClick={() => setCurrentFilter(colKey, '')}
                                className="absolute right-1 text-slate-400 hover:text-slate-600"
                                title="Xóa lọc cột này"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            ) : null}
                          </div>
                        </th>
                      )
                    })}
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 bg-white">
                  {currentFilteredRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={currentColumns.length}
                        className="py-12 text-center text-sm text-slate-500"
                      >
                        <div className="flex flex-col items-center justify-center gap-2">
                          <Search className="h-8 w-8 text-slate-300" />
                          <p>Không có dòng nào khớp với bộ lọc hiện tại.</p>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={resetCurrentTabFilters}
                            className="mt-1 gap-1 text-xs"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Xóa bộ lọc
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentFilteredRows.map((row, idx) => (
                      <tr
                        key={row.id}
                        className="hover:bg-slate-50/80 transition-colors"
                      >
                        {currentColumns.map((col) => {
                          const alignClass =
                            col.align === 'right'
                              ? 'text-right'
                              : col.align === 'center'
                                ? 'text-center'
                                : 'text-left'
                          return (
                            <td
                              key={String(col.id)}
                              className={`px-2.5 py-2 text-xs whitespace-nowrap ${alignClass}`}
                            >
                              {col.render
                                ? (col.render as any)(row, idx)
                                : String((row as any)[col.id] ?? '')}
                            </td>
                          )
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
export default BomAggregatePage
